import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { mockCampsites } from '@/data/mockCampsites';
import { MapMarker } from '@/assets/icon';
import campOverlay from '../../../assets/camp_overlay.png';

// Set Mapbox access token
mapboxgl.accessToken = 'pk.eyJ1IjoiZGFmaWRhZmkyNSIsImEiOiJjbTdsczg0Y3QwYnEwMmpwemIxZ2h4dHY0In0.dcTST_LLYTlp-t_oUkB0dw';

const HeatmapMode: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Initialize map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [114.25, 22.45], // Hong Kong coordinates
      zoom: 10
    });

    map.current.on('load', () => {
      if (!map.current) return;

      // Generate heatmap data points based on campsites with more density
      const heatmapPoints = mockCampsites.flatMap(campsite => {
        const points = [];
        const occupancyWeight = campsite.occupancy.percentage / 100;

        // Create multiple points around each campsite based on occupancy
        const numPoints = Math.floor(20 + (occupancyWeight * 30)); // 20-50 points per campsite

        for (let i = 0; i < numPoints; i++) {
          // Spread points in a radius around the campsite
          const radius = 0.01 + (Math.random() * 0.02); // ~1-3km radius
          const angle = Math.random() * Math.PI * 2;
          const lat = campsite.coordinates.lat + (Math.cos(angle) * radius);
          const lng = campsite.coordinates.lng + (Math.sin(angle) * radius);

          points.push({
            type: 'Feature',
            properties: {
              intensity: occupancyWeight + (Math.random() * 0.3) // Add some variation
            },
            geometry: {
              type: 'Point',
              coordinates: [lng, lat]
            }
          });
        }

        return points;
      });

      // Add heatmap source
      map.current.addSource('crowd-heatmap', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: heatmapPoints as any
        }
      });

      // Add heatmap layer
      map.current.addLayer({
        id: 'crowd-heatmap-layer',
        type: 'heatmap',
        source: 'crowd-heatmap',
        paint: {
          // Increase weight as diameter breast height increases
          'heatmap-weight': [
            'interpolate',
            ['linear'],
            ['get', 'intensity'],
            0, 0,
            1, 1
          ],
          // Increase intensity as zoom level increases
          'heatmap-intensity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0, 1,
            15, 3
          ],
          // Color ramp for heatmap (blue -> green -> yellow -> orange -> red)
          'heatmap-color': [
            'interpolate',
            ['linear'],
            ['heatmap-density'],
            0, 'rgba(33,102,172,0)',
            0.2, 'rgb(103,169,207)',
            0.4, 'rgb(209,229,240)',
            0.6, 'rgb(253,219,199)',
            0.8, 'rgb(239,138,98)',
            1, 'rgb(178,24,43)'
          ],
          // Adjust the heatmap radius by zoom level
          'heatmap-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0, 2,
            15, 20
          ],
          // Transition from heatmap to circle layer by zoom level
          'heatmap-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7, 1,
            15, 0.5
          ]
        }
      });

      // Load marker image
      const markerImg = new Image();
      markerImg.src = MapMarker;
      markerImg.onload = () => {
        if (!map.current) return;

        if (!map.current.hasImage('custom-marker')) {
          map.current.addImage('custom-marker', markerImg);
        }

        // Add campsite markers on top of heatmap
        map.current.addSource('campsites', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: mockCampsites.map(campsite => ({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [campsite.coordinates.lng, campsite.coordinates.lat]
              },
              properties: {
                id: campsite.id,
                name: campsite.name,
                occupancy: `${campsite.occupancy.current}/${campsite.occupancy.total}`,
                occupancyCurrent: campsite.occupancy.current,
                occupancyTotal: campsite.occupancy.total,
                image: campsite.images[0] || ''
              }
            }))
          }
        });

        // Add campsite marker layer using custom icon
        map.current.addLayer({
          id: 'campsite-markers',
          type: 'symbol',
          source: 'campsites',
          layout: {
            'icon-image': 'custom-marker',
            'icon-size': 0.8,
            'icon-allow-overlap': true
          }
        });

        // Click on campsite marker to show popup
        map.current.on('click', 'campsite-markers', (e) => {
          if (!e.features || e.features.length === 0 || !map.current) return;

          const feature = e.features[0];
          if (!feature) return;

          const geometry = feature.geometry as any;
          if (!geometry?.coordinates) return;

          const coordinates = geometry.coordinates.slice();
          const properties = feature.properties;

          // Get occupancy color
          const occupancyPercentage = (properties?.occupancyCurrent / properties?.occupancyTotal) * 100;
          let occupancyColor = '#719C40'; // green
          if (occupancyPercentage >= 90) occupancyColor = '#F53743'; // red
          else if (occupancyPercentage >= 70) occupancyColor = '#FFB230'; // orange

          // Create popup with camp overlay background
          new mapboxgl.Popup({
            offset: 25,
            className: 'camp-popup',
            closeButton: false,
            maxWidth: 'none'
          })
            .setLngLat(coordinates)
            .setHTML(`
              <div style="
                background-image: url('${campOverlay}');
                background-size: cover;
                background-position: center;
                border-radius: 16px;
                overflow: hidden;
                width: 304px;
                box-shadow: 0 4px 16px rgba(0,0,0,0.15);
              ">
                <img src="${properties?.image}" alt="${properties?.name}" style="
                  width: 100%;
                  height: 140px;
                  object-fit: cover;
                  border-radius: 16px 16px 0 0;
                " />
                <div style="padding: 12px 16px 16px 16px; background: white; border-radius: 0 0 16px 16px;">
                  <h3 style="
                    font-family: Poppins;
                    font-size: 18px;
                    font-weight: 700;
                    margin: 0 0 8px 0;
                    color: #000000;
                    line-height: 1.2;
                  ">${properties?.name}</h3>
                  <div style="
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 10px;
                  ">
                    <span style="
                      font-family: Inter;
                      font-size: 14px;
                      font-weight: 400;
                      color: #000000;
                    ">Occupancy: ${properties?.occupancyCurrent}/${properties?.occupancyTotal}</span>
                    <div style="
                      width: 12px;
                      height: 12px;
                      border-radius: 50%;
                      background-color: ${occupancyColor};
                    "></div>
                  </div>
                  <div style="display: flex; justify-content: flex-end;">
                    <button style="
                      background: #000000;
                      color: white;
                      border: none;
                      border-radius: 16px;
                      padding: 8px 24px;
                      font-family: Inter;
                      font-size: 13px;
                      font-weight: 500;
                      cursor: pointer;
                    ">more</button>
                  </div>
                </div>
              </div>
            `)
            .addTo(map.current);
        });

        // Change cursor on hover
        map.current.on('mouseenter', 'campsite-markers', () => {
          if (map.current) map.current.getCanvas().style.cursor = 'pointer';
        });
        map.current.on('mouseleave', 'campsite-markers', () => {
          if (map.current) map.current.getCanvas().style.cursor = '';
        });
      };
    });

    // Clean up on unmount
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  return (
    <div className="w-full h-[700px] rounded-xl overflow-hidden">
      <div ref={mapContainer} className="w-full h-full" />
    </div>
  );
};

export default HeatmapMode;
